import { QuickResolveActionItem } from '@hakimo-ui/hakimo/types';
import { Button, InputField, Modal } from '@hakimo-ui/shared/ui-base';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { isQuickResolveAction } from '../type-guards';

interface CreateUpdateQuickResolveModalProps {
  onClose: () => void;
  action: QuickResolveActionItem;
  onSubmit: (action: QuickResolveActionItem) => void;
  isNew?: boolean;
}

function CreateUpdateQuickResolveModal(
  props: CreateUpdateQuickResolveModalProps
) {
  const { onClose, action, onSubmit, isNew } = props;

  // Initialize state based on whether action is string or object
  const initialLabel = isQuickResolveAction(action) ? action.label : action;
  const initialSubActions = isQuickResolveAction(action)
    ? action.subActions ?? []
    : [];

  const [label, setLabel] = useState(initialLabel);
  const [subActions, setSubActions] = useState<string[]>(initialSubActions);

  const onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  };

  const onSubActionChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const updatedSubActions = [...subActions];
      updatedSubActions[index] = value;
      setSubActions(updatedSubActions);
    };

  const onAddSubAction = () => {
    setSubActions([...subActions, '']);
  };

  const onDeleteSubAction = (index: number) => () => {
    const updatedSubActions = [...subActions];
    updatedSubActions.splice(index, 1);
    setSubActions(updatedSubActions);
  };

  const onSubmitUpdatedValue = () => {
    // Filter out empty sub-actions
    const cleanedSubActions = subActions.filter((sub) => sub.trim() !== '');

    // If there are sub-actions, return object format, otherwise return string
    if (cleanedSubActions.length > 0) {
      onSubmit({ label, subActions: cleanedSubActions });
    } else {
      onSubmit(label);
    }
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onSubmitUpdatedValue}
        disabled={label.trim() === ''}
      >
        Submit
      </Button>
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      title={
        isNew
          ? 'Create new Quick Resolve Action'
          : 'Update Quick Resolve Action'
      }
      open
      onClose={onClose}
      footer={actions}
    >
      <div className="min-w-[50rem] space-y-4 p-4 px-8">
        <InputField
          value={label}
          type="text"
          label="Action Label"
          onChange={onLabelChange}
          autoFocus
          placeholder="e.g., Authorized Person"
        />

        <div>
          <div className="dark:text-dark-secondary-text mb-2 block text-sm text-gray-700">
            Sub-Actions (Optional)
          </div>
          <p className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 text-xs">
            Add sub-actions to create a hierarchical quick resolve menu. Leave
            empty for a simple action.
          </p>
          <div className="space-y-2">
            {subActions.map((subAction, index) => (
              <div key={index} className="flex items-center gap-2">
                <InputField
                  value={subAction}
                  type="text"
                  placeholder="e.g., Employee, Contractor, Visitor"
                  onChange={onSubActionChange(index)}
                />
                <Button variant="icon" onClick={onDeleteSubAction(index)}>
                  <TrashIcon className="h-5 w-5" />
                </Button>
              </div>
            ))}
            <Button onClick={onAddSubAction} variant="outline">
              Add Sub-Action
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CreateUpdateQuickResolveModal;
